import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const artists = [
  { name: "Diskonnected", sub: "Taiwan", featured: true, href: "/artist/diskonnected" },
  { name: "OCCA", sub: "Japan", featured: false, href: "/artist/occa" },
  { name: "Iron", sub: "Japan", featured: false, href: "/artist/iron" },
];

const space = [
  { role: "Decoration", names: "Koichi Hashiguchi  /  Beni Shibari" },
  { role: "Lighting", names: "Ryung" },
  { role: "Incense", names: "Souta Shinozaki" },
];

const other = [
  { role: "Experience", names: "ZEN HERB  /  SAW  /  Mind_gal" },
  { role: "Food", names: "Iikamo Fruit" },
];

export default function LineupSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  /* ── Fractal grid canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      /* faint grid lines — coordinate system feel */
      const COLS = 12;
      const ROWS = 16;
      for (let c = 0; c <= COLS; c++) {
        const x = (c / COLS) * W;
        const pulse = Math.sin(t * 0.012 + c * 0.4) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.strokeStyle = `rgba(255,220,140,${0.015 + pulse * 0.02})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      for (let r = 0; r <= ROWS; r++) {
        const y = (r / ROWS) * H;
        const pulse = Math.sin(t * 0.01 + r * 0.3) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.strokeStyle = `rgba(255,220,140,${0.01 + pulse * 0.015})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* intersection glow nodes */
      for (let c = 1; c < COLS; c += 3) {
        for (let r = 1; r < ROWS; r += 4) {
          const x = (c / COLS) * W;
          const y = (r / ROWS) * H;
          const pulse = Math.sin(t * 0.015 + c * 0.7 + r * 0.5) * 0.5 + 0.5;
          const g = ctx.createRadialGradient(x, y, 0, x, y, 8);
          g.addColorStop(0, `rgba(255,220,140,${pulse * 0.18})`);
          g.addColorStop(1, "rgba(255,200,100,0)");
          ctx.beginPath();
          ctx.arc(x, y, 8, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }
      }

      /* diagonal fractal lines */
      for (let i = 0; i < 5; i++) {
        const progress = ((t * 0.003 + i * 0.2) % 1);
        const x = progress * W * 1.5 - W * 0.25;
        const alpha = Math.sin(progress * Math.PI) * 0.06;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x + H * 0.3, H);
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      t++;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener("resize", resize); };
  }, []);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".lineup-reveal");
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = "1";
                (item as HTMLElement).style.transform = "translateY(0)";
              }, i * 130);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="lineup"
      ref={sectionRef}
      className="relative w-full flex items-center overflow-hidden"
      style={{ background: "#060606" }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-20 py-24 md:py-40">
        {/* section label */}
        <div className="flex items-center gap-4 mb-16 md:mb-24">
          <div style={{ width: "24px", height: "1px", background: "rgba(255,220,140,0.3)" }} />
          <p
            className="text-white/15 tracking-[0.5em] uppercase text-xs"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            lineup
          </p>
        </div>

        {/* Artists */}
        <div className="flex flex-col mb-20 md:mb-28">
          {artists.map((artist, i) => (
            <div
              key={i}
              className="lineup-reveal group relative py-5 md:py-7 border-b overflow-hidden"
              style={{
                borderColor: "rgba(255,220,140,0.06)",
                opacity: 0,
                transform: "translateY(18px)",
                transition: "opacity 1.3s ease, transform 1.3s ease",
                cursor: artist.href ? "pointer" : "default",
              }}
              onClick={() => artist.href && navigate(artist.href)}
            >
              {/* hover glow bar */}
              <div
                className="absolute left-0 top-0 bottom-0 w-0 group-hover:w-full transition-all duration-700 pointer-events-none"
                style={{ background: "linear-gradient(to right, rgba(255,220,140,0.03), transparent)" }}
              />

              {/* index */}
              <span
                className="absolute right-0 top-1/2 -translate-y-1/2 text-white/[0.06] font-light"
                style={{ fontFamily: "'Courier New', monospace", fontSize: "clamp(40px,6vw,80px)" }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>

              <div className="flex items-center gap-3 md:gap-5">
                {/* DNA node */}
                <div
                  style={{
                    width: artist.featured ? "8px" : "5px",
                    height: artist.featured ? "8px" : "5px",
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: artist.featured ? "rgba(255,220,140,0.7)" : "rgba(255,220,140,0.2)",
                    boxShadow: artist.featured ? "0 0 12px 3px rgba(255,200,100,0.3)" : "none",
                  }}
                />
                <div className="flex items-baseline gap-2 md:gap-5 flex-1 min-w-0">
                  <p
                    className="text-white font-light tracking-wider truncate"
                    style={{
                      fontSize: artist.featured ? "clamp(20px, 4.5vw, 64px)" : "clamp(15px, 2.8vw, 40px)",
                      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                      fontWeight: 300,
                      letterSpacing: "0.06em",
                      color: artist.featured ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.5)",
                      textShadow: artist.featured ? "0 0 40px rgba(255,220,140,0.15)" : "none",
                    }}
                  >
                    {artist.name}
                    {artist.sub && (
                      <span
                        className="ml-2 md:ml-5"
                        style={{
                          fontSize: "clamp(9px, 0.9vw, 13px)",
                          letterSpacing: "0.2em",
                          color: "rgba(255,220,140,0.3)",
                          fontFamily: "'Courier New', monospace",
                        }}
                      >
                        {artist.sub}
                      </span>
                    )}
                  </p>
                  {artist.href && (
                    <span
                      className="hidden md:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{
                        fontSize: "10px",
                        letterSpacing: "0.3em",
                        color: "rgba(255,220,140,0.4)",
                        fontFamily: "'Courier New', monospace",
                        textTransform: "uppercase",
                      }}
                    >
                      Profile
                      <i className="ri-arrow-right-line" style={{ fontSize: "10px" }} />
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Space + Other */}
        <div className="flex flex-col gap-12 md:grid md:grid-cols-2 md:gap-16">
          <div
            className="lineup-reveal flex flex-col gap-6 md:gap-7"
            style={{ opacity: 0, transform: "translateY(18px)", transition: "opacity 1.3s ease, transform 1.3s ease" }}
          >
            {/* column header */}
            <div className="flex items-center gap-3 mb-2">
              <div style={{ width: "16px", height: "1px", background: "rgba(255,220,140,0.25)" }} />
              <span className="text-white/10 text-[10px] tracking-[0.4em] uppercase" style={{ fontFamily: "'Courier New', monospace" }}>space</span>
            </div>
            {space.map((el, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-2 sm:gap-6 sm:items-baseline">
                <span
                  className="text-white/20 text-xs tracking-[0.3em] uppercase flex-shrink-0 sm:w-24"
                  style={{ fontFamily: "'Courier New', monospace" }}
                >
                  {el.role}
                </span>
                <span
                  className="text-white/45 font-light text-sm"
                  style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 300, letterSpacing: "0.04em" }}
                >
                  {el.names}
                </span>
              </div>
            ))}
          </div>

          <div
            className="lineup-reveal flex flex-col gap-6 md:gap-7"
            style={{ opacity: 0, transform: "translateY(18px)", transition: "opacity 1.3s ease, transform 1.3s ease" }}
          >
            <div className="flex items-center gap-3 mb-2">
              <div style={{ width: "16px", height: "1px", background: "rgba(255,220,140,0.25)" }} />
              <span className="text-white/10 text-[10px] tracking-[0.4em] uppercase" style={{ fontFamily: "'Courier New', monospace" }}>other</span>
            </div>
            {other.map((el, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-2 sm:gap-6 sm:items-baseline">
                <span
                  className="text-white/20 text-xs tracking-[0.3em] uppercase flex-shrink-0 sm:w-24"
                  style={{ fontFamily: "'Courier New', monospace" }}
                >
                  {el.role}
                </span>
                <span
                  className="text-white/45 font-light text-sm"
                  style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 300, letterSpacing: "0.04em" }}
                >
                  {el.names}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
